import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * Zeigt eine gelbe Warnzeile, wenn ein aus Telesales-Lead entstandenes Projekt
 * unvollständige Customer- oder Verbrauchsdaten hat. Ein Klick auf
 * „Aus Telesales-Lead nachziehen" ruft `POST /api/projects/{id}/refresh-from-lead`
 * auf und lädt anschließend das Projekt neu.
 *
 * Idempotent: überschreibt keine bereits gesetzten Werte.
 * Wird nur angezeigt, wenn `project.source === "telesales_lead"` und mind.
 * ein relevantes Feld fehlt.
 */
export default function TelesalesDataCheck({ project, customer, consumption, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const missing = useMemo(() => {
    if (!project || project.source !== "telesales_lead") return [];
    const gaps = [];
    if (!customer?.current_heating) gaps.push("Heizungsart");
    if (!customer?.electricity_kwh_per_year) gaps.push("Strom-Verbrauch");
    if (!customer?.living_area_sqm && !project.heated_area) gaps.push("Wohnfläche");
    if (!customer?.occupants) gaps.push("Personen im Haushalt");
    const noConsumption = !consumption || (
      !consumption.annual_consumption_kwh &&
      !consumption.annual_consumption_gas_m3 &&
      !consumption.annual_consumption_oil_liters &&
      !consumption.annual_consumption_pellets_kg
    );
    if (noConsumption) gaps.push("Jahres-Verbrauch");
    return gaps;
  }, [project, customer, consumption]);

  if (missing.length === 0) return null;

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/refresh-from-lead`);
      const parts = [];
      if (data.customer_fields_updated?.length) {
        parts.push(`${data.customer_fields_updated.length} Kunden-Felder gefüllt`);
      }
      if (data.consumption_row_created) parts.push("Verbrauch angelegt");
      if (parts.length === 0) parts.push("Keine neuen Daten im Lead vorhanden");
      toast.success(`Nachziehen fertig: ${parts.join(" · ")}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nachziehen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3.5 flex flex-col sm:flex-row items-start gap-3"
      data-testid="telesales-datacheck-widget"
    >
      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <AlertTriangle size={16} className="text-amber-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-amber-200 text-[13.5px]">
          Kunden-Daten unvollständig · aus Telesales-Lead nachziehen
        </div>
        <p className="text-[12px] text-amber-100/85 mt-0.5 leading-snug font-medium">
          Fehlend: {missing.join(", ")}. Der Lead enthält diese Felder ggf. bereits — 1-Klick genügt.
        </p>
      </div>
      <button
        onClick={refresh}
        disabled={busy}
        data-testid="telesales-datacheck-refresh"
        className="h-9 px-3.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        Nachziehen
      </button>
    </div>
  );
}
